import {
  determineUserLevelFromClaims,
  DOMAIN_CODE_LABELS,
  DomainCode,
  domainCodeAtom,
} from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';

import { getAgcGeoRoles, getUserDetails } from '@/apis/userDetails';
import { ApiBodyError } from '@/errors/ApiBodyError';
import { useFeatureToggle } from '@/featureToggles/FeatureTogglesContext';
import { UserDetailsWithLevel, UserLevel } from '@/types';
import { BUSINESS_ROLE_MAPPING, determineUserLevel, logger, userLevelPriority } from '@/util';
import { setTags } from '@sentry/react';
import { useAtomValue } from 'jotai';

/* If useGetUserDetailsForUser fails, this is the method it returns. */

export const DEFAULT_USER_DETAILS_WITH_LEVEL: UserDetailsWithLevel = {
  sub: '',
  name: '',
  rolesByDomain: {},
  userLevel: 'None',
  contactId: -1,
  drupalId: -1,
  domainToUserLevels: {},
};

function isUserNoDomainError(err: unknown) {
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    err instanceof ApiBodyError && (err.body as any)?.errors?.[0]?.name === 'UserNoDomainError'
  );
}

const fetchDomainCodes = async (token: string) => {
  try {
    const userDetails = await getUserDetails(token);
    logger.debug(`user domains: ${userDetails}`);
    return userDetails;
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') {
      logger.debug('Previous getUserDetails request was cancelled');
      return DEFAULT_USER_DETAILS_WITH_LEVEL;
    } else if (isUserNoDomainError(err)) {
      logger.debug('User has no domains');
      return DEFAULT_USER_DETAILS_WITH_LEVEL;
    } else {
      throw err;
    }
  }
};

export const useUserDetails = () => {
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const isBusinessRoleAuthorisationEnabled = useFeatureToggle('Business Role Authorisation');

  return useQuery({
    queryKey: ['userDetails', domainCode],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const userDetails = await fetchDomainCodes(token);
      const domains = Object.keys(DOMAIN_CODE_LABELS);
      const domainToUserLevels = {} as Record<string, UserLevel>;
      console.log('userDetails', userDetails);

      const mapDomainToUserLevel = async (domainCodeIn: DomainCode | undefined) =>
        isBusinessRoleAuthorisationEnabled
          ? determineUserLevelFromClaims(
              await getIdTokenClaims(),
              domainCodeIn ?? '',
              BUSINESS_ROLE_MAPPING,
              userLevelPriority
            )
          : determineUserLevel(userDetails, domainCodeIn);

      const userLevel = await mapDomainToUserLevel(domainCode);
      for (const domain of domains) {
        domainToUserLevels[domain] = await mapDomainToUserLevel(domain as DomainCode | undefined);
      }

      setTags({
        'user-role': userLevel,
        'contact-id': userDetails.contactId,
        'drupal-id': userDetails.drupalId,
        'user-sub': userDetails.sub,
      });
      return { ...userDetails, userLevel, domainToUserLevels };
    },
    staleTime: 120000,
  });
};

export const useAgcGeoRoles = () => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);

  return useQuery({
    queryKey: ['agcGeoRoles', domainCode],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const ourAgcGeoRoles = await getAgcGeoRoles(token, domainCode);
      return ourAgcGeoRoles;
    },
    staleTime: 120000,
  });
};
